import { Controller } from "@hotwired/stimulus"

// iOS-style Segmented Control Controller
// A reusable component following Apple HIG specifications
export default class extends Controller {
  static targets = ["background", "option"]
  static values = {
    selected: { type: Number, default: 0 },
    animated: { type: Boolean, default: true }
  }
  static classes = ["active"]

  connect() {
    this.initialize()
    this.setupAccessibility()
    this.setupKeyboardNavigation()
    this.setupResizeObserver()
  }

  initialize() {
    this.select(this.selectedValue, false)

    // Add iOS-style touch feedback for mobile
    if ('ontouchstart' in window) {
      this.setupTouchFeedback()
    }
  }

  setupAccessibility() {
    // Add ARIA attributes for screen readers
    this.element.setAttribute('role', 'radiogroup')
    this.element.setAttribute('aria-label', this.data.get('label') || 'Selection options')

    this.optionTargets.forEach((option, index) => {
      option.setAttribute('role', 'radio')
      option.setAttribute('tabindex', index === this.selectedValue ? '0' : '-1')
      option.setAttribute('aria-checked', index === this.selectedValue ? 'true' : 'false')

      // Add unique ID if not present
      if (!option.id) {
        option.id = `segmented-option-${this.element.id || Math.random().toString(36).substr(2, 9)}-${index}`
      }
    })
  }

  setupKeyboardNavigation() {
    this.element.addEventListener('keydown', this.handleKeydown.bind(this))
  }

  setupResizeObserver() {
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => {
        this.updateBackground(this.selectedValue, false)
      })
      this.resizeObserver.observe(this.element)
    }
  }

  setupTouchFeedback() {
    this.optionTargets.forEach(option => {
      option.addEventListener('touchstart', () => {
        option.style.transform = 'scale(0.98)'
      })

      option.addEventListener('touchend', () => {
        setTimeout(() => {
          option.style.transform = ''
        }, 100)
      })
    })
  }

  handleKeydown(event) {
    const currentIndex = this.selectedValue
    let newIndex = currentIndex

    switch(event.key) {
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault()
        newIndex = currentIndex > 0 ? currentIndex - 1 : this.optionTargets.length - 1
        break

      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault()
        newIndex = currentIndex < this.optionTargets.length - 1 ? currentIndex + 1 : 0
        break

      case ' ':
      case 'Enter':
        event.preventDefault()

        const focusedOption = document.activeElement
        const focusedIndex = this.optionTargets.indexOf(focusedOption)
        if (focusedIndex !== -1 && focusedIndex !== currentIndex) {
          newIndex = focusedIndex
        }
        break

      default:
        return
    }

    if (newIndex !== currentIndex) {
      this.select(newIndex)
      this.optionTargets[newIndex].focus()
    }
  }


  selectOption(event) {
    const option = event.currentTarget
    const index = this.optionTargets.indexOf(option)

    if (index !== -1 && index !== this.selectedValue) {
      this.select(index)
    }
  }

  select(index, animate = true) {
    if (index < 0 || index >= this.optionTargets.length) return

    const previousIndex = this.selectedValue
    this.selectedValue = index

    this.updateBackground(index, animate && previousIndex !== undefined)
    this.updateOptions(index)
    this.updateAccessibility(index)

    this.dispatch('change', {
      detail: {
        index: index,
        value: this.optionTargets[index].dataset.value || index,
        label: this.optionTargets[index].textContent.trim(),
        previousIndex: previousIndex
      }
    })
  }

  updateBackground(index, animate = true) {
    if (!this.hasBackgroundTarget) return

    const segmentCount = this.optionTargets.length

    const containerWidth = this.element.offsetWidth
    const padding = 3
    const availableWidth = containerWidth - (padding * 2)
    const segmentWidth = availableWidth / segmentCount
    const offset = segmentWidth * index + padding

    // Use translateX with pixel values for iOS Safari compatibility
    const transform = `translateX(${offset}px)`

    if (animate) {
      // iOS-style spring animation
      this.backgroundTarget.style.transition = 'transform 0.35s cubic-bezier(0.25, 0.46, 0.45, 0.94), width 0.35s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
    } else {
      this.backgroundTarget.style.transition = 'none'
    }

    // Use explicit width and transform for better iOS compatibility
    this.backgroundTarget.style.transform = transform
    this.backgroundTarget.style.width = `${segmentWidth}px`
    this.backgroundTarget.style.left = '0' // Reset left positioning
  }

  updateOptions(selectedIndex) {
    this.optionTargets.forEach((option, index) => {
      const isSelected = index === selectedIndex

      // Update classes
      if (this.hasActiveClass) {
        option.classList.toggle(this.activeClass, isSelected)
      } else {
        option.classList.toggle('active', isSelected)
      }

      // iOS-style text color change
      if (isSelected) {
        option.style.color = '' // Use CSS default
      }
    })
  }

  updateAccessibility(selectedIndex) {
    this.optionTargets.forEach((option, index) => {
      const isSelected = index === selectedIndex
      option.setAttribute('aria-checked', isSelected ? 'true' : 'false')
      option.setAttribute('tabindex', isSelected ? '0' : '-1')
    })
  }

  selectedValueChanged(value, previousValue) {
    if (value !== previousValue && this.hasBeenConnected) {
      this.select(value, true)
    }
  }

  disconnect() {
    this.element.removeEventListener('keydown', this.handleKeydown)

    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    }
  }

  get hasBeenConnected() {
    return this._connected || false
  }

  set hasBeenConnected(value) {
    this._connected = value
  }
}